import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface CertificateHistoryProps {
  domain: string;
}

interface GeneratedCertificate {
  id: string;
  studentId: string;
  studentName: string;
  enrollmentId: string;
  class: string;
  section: string;
  rollNumber: string;
  templateId: string;
  templateName: string;
  templateUrl: string;
  customFields: Record<string, string>;
  generatedAt: Date;
  generatedBy: string;
}

interface CertificateTemplate {
  id: string;
  name: string;
  templateUrl: string;
}

const CertificateHistory: React.FC<CertificateHistoryProps> = ({ domain }) => {
  const [certificates, setCertificates] = useState<GeneratedCertificate[]>([]);
  const [templates, setTemplates] = useState<CertificateTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Filters
  const [filterTemplate, setFilterTemplate] = useState<string>('');
  const [filterClass, setFilterClass] = useState<string>('');
  const [filterSection, setFilterSection] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Unique values for filters
  const [classes, setClasses] = useState<string[]>([]);
  const [sections, setSections] = useState<string[]>([]);

  // Selection for bulk actions
  const [selectedCertificates, setSelectedCertificates] = useState<Set<string>>(new Set());

  // View modal
  const [viewCertificate, setViewCertificate] = useState<GeneratedCertificate | null>(null);

  useEffect(() => {
    if (domain) {
      loadCertificates();
      loadTemplates();
    }
  }, [domain]);

  const loadCertificates = async () => {
    setLoading(true);
    try {
      const certificatesRef = collection(db, `school-data/${domain}/generated-certificates`);
      const q = query(certificatesRef, orderBy('generatedAt', 'desc'));
      const snapshot = await getDocs(q);

      const certList: GeneratedCertificate[] = [];
      const uniqueClasses = new Set<string>();
      const uniqueSections = new Set<string>();

      snapshot.forEach((doc) => {
        const data = doc.data();
        const cert: GeneratedCertificate = {
          id: doc.id,
          studentId: data.studentId,
          studentName: data.studentName,
          enrollmentId: data.enrollmentId,
          class: data.class,
          section: data.section,
          rollNumber: data.rollNumber || '',
          templateId: data.templateId,
          templateName: data.templateName,
          templateUrl: data.templateUrl || '',
          customFields: data.customFields || {},
          generatedAt: data.generatedAt?.toDate() || new Date(),
          generatedBy: data.generatedBy || 'Unknown',
        };
        certList.push(cert);
        uniqueClasses.add(cert.class);
        uniqueSections.add(cert.section);
      });

      setCertificates(certList);
      setClasses([...uniqueClasses].sort((a, b) => parseInt(a) - parseInt(b)));
      setSections([...uniqueSections].sort());
    } catch (err) {
      console.error('Error loading certificates:', err);
      setError('Failed to load certificate history');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const templatesRef = collection(db, `school-data/${domain}/certificate-templates`);
      const snapshot = await getDocs(templatesRef);
      const templateList: CertificateTemplate[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        templateList.push({
          id: doc.id,
          name: data.name,
          templateUrl: data.templateUrl || '',
        });
      });
      setTemplates(templateList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const filteredCertificates = certificates.filter((cert) => {
    // Filter by template
    if (filterTemplate && cert.templateId !== filterTemplate) return false;

    // Filter by class
    if (filterClass && cert.class !== filterClass) return false;

    // Filter by section
    if (filterSection && cert.section !== filterSection) return false;

    // Filter by search term (name or enrollment ID)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!cert.studentName.toLowerCase().includes(searchLower) &&
          !cert.enrollmentId.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Filter by date range
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (cert.generatedAt < fromDate) return false;
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (cert.generatedAt > toDate) return false;
    }

    return true;
  });

  const handleSelectAll = () => {
    if (selectedCertificates.size === filteredCertificates.length) {
      setSelectedCertificates(new Set());
    } else {
      setSelectedCertificates(new Set(filteredCertificates.map(c => c.id)));
    }
  };

  const handleSelectCertificate = (id: string) => {
    const newSelected = new Set(selectedCertificates);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedCertificates(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedCertificates.size === 0) return;

    const confirmMessage = selectedCertificates.size === 1
      ? 'Are you sure you want to delete this certificate record?'
      : `Are you sure you want to delete ${selectedCertificates.size} certificate records?`;

    if (!window.confirm(confirmMessage)) return;

    setLoading(true);
    try {
      let deleted = 0;
      for (const id of selectedCertificates) {
        try {
          await deleteDoc(doc(db, `school-data/${domain}/generated-certificates`, id));
          deleted++;
        } catch (err) {
          console.error(`Error deleting certificate ${id}:`, err);
        }
      }
      setSuccess(`Successfully deleted ${deleted} certificate record(s)`);
      setSelectedCertificates(new Set());
      await loadCertificates();
    } catch (err) {
      console.error('Error deleting certificates:', err);
      setError('Failed to delete certificates');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSingle = async (cert: GeneratedCertificate) => {
    if (!window.confirm(`Delete certificate for ${cert.studentName}?`)) return;

    try {
      await deleteDoc(doc(db, `school-data/${domain}/generated-certificates`, cert.id));
      setSuccess('Certificate record deleted');
      await loadCertificates();
    } catch (err) {
      console.error('Error deleting certificate:', err);
      setError('Failed to delete certificate');
    }
  };

  const clearFilters = () => {
    setFilterTemplate('');
    setFilterClass('');
    setFilterSection('');
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  const getSchoolName = (): string => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.schoolName || parsed.domain || 'School Name';
      }
    } catch {
      // ignore
    }
    return 'School Name';
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCustomFieldLabel = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  // Export certificates to CSV
  const exportToCSV = () => {
    const headers = ['Student Name', 'Enrollment ID', 'Class', 'Section', 'Roll Number', 'Template', 'Generated At'];
    const rows = filteredCertificates.map(cert => [
      cert.studentName,
      cert.enrollmentId,
      cert.class,
      cert.section,
      cert.rollNumber,
      cert.templateName,
      formatDate(cert.generatedAt),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `certificates_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Error/Success Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}
      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Filters Card */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <i className="bi bi-funnel me-2"></i>
            Filter Certificates
          </h6>
          <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
            <i className="bi bi-x-circle me-1"></i>
            Clear Filters
          </button>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label small">Search</label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Template</label>
              <select
                className="form-select"
                value={filterTemplate}
                onChange={(e) => setFilterTemplate(e.target.value)}
              >
                <option value="">All Templates</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Class</label>
              <select
                className="form-select"
                value={filterClass}
                onChange={(e) => setFilterClass(e.target.value)}
              >
                <option value="">All Classes</option>
                {classes.map((c) => (
                  <option key={c} value={c}>Class {c}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">Section</label>
              <select
                className="form-select"
                value={filterSection}
                onChange={(e) => setFilterSection(e.target.value)}
              >
                <option value="">All Sections</option>
                {sections.map((s) => (
                  <option key={s} value={s}>Section {s}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small">Date Range</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
                <span className="input-group-text">to</span>
                <input
                  type="date"
                  className="form-control"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Table */}
      <div className="card shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">
              <i className="bi bi-clock-history me-2"></i>
              Generated Certificates
              {filteredCertificates.length > 0 && (
                <span className="badge bg-secondary ms-2">{filteredCertificates.length}</span>
              )}
            </h6>
          </div>
          <div className="d-flex gap-2">
            {selectedCertificates.size > 0 && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={handleDeleteSelected}
              >
                <i className="bi bi-trash me-1"></i>
                Delete Selected ({selectedCertificates.size})
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={exportToCSV}
              disabled={filteredCertificates.length === 0}
            >
              <i className="bi bi-download me-1"></i>
              Export CSV
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
              <p className="text-muted mt-3">Loading certificates...</p>
            </div>
          ) : filteredCertificates.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-3"></i>
              <p className="mb-0">
                {certificates.length === 0
                  ? 'No certificates generated yet'
                  : 'No certificates match your filters'
                }
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedCertificates.size === filteredCertificates.length && filteredCertificates.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Template</th>
                    <th>Generated</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCertificates.map((cert) => (
                    <tr key={cert.id} className={selectedCertificates.has(cert.id) ? 'table-primary' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedCertificates.has(cert.id)}
                          onChange={() => handleSelectCertificate(cert.id)}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '36px', height: '36px' }}>
                            <i className="bi bi-award"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">{cert.studentName}</div>
                            <small className="text-muted">
                              <code>{cert.enrollmentId}</code>
                              {cert.rollNumber && <span className="ms-2">| Roll: {cert.rollNumber}</span>}
                            </small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark">
                          {cert.class}-{cert.section}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-primary-subtle text-primary">
                          {cert.templateName}
                        </span>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(cert.generatedAt)}
                        </small>
                      </td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => setViewCertificate(cert)}
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          {cert.templateUrl && (
                            <a
                              href={cert.templateUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-outline-secondary"
                              title="View Template"
                            >
                              <i className="bi bi-file-earmark-image"></i>
                            </a>
                          )}
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDeleteSingle(cert)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {filteredCertificates.length > 0 && (
          <div className="card-footer bg-white text-muted small">
            Showing {filteredCertificates.length} of {certificates.length} certificates
          </div>
        )}
      </div>

      {/* View Certificate Modal */}
      {viewCertificate && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-award me-2"></i>
                  Certificate Details
                </h5>
                <button type="button" className="btn-close" onClick={() => setViewCertificate(null)}></button>
              </div>
              <div className="modal-body">
                {viewCertificate.templateUrl && (
                  <div className="text-center mb-4">
                    <img
                      src={viewCertificate.templateUrl}
                      alt="Certificate Template"
                      className="img-fluid border rounded shadow-sm"
                      style={{ maxHeight: '250px' }}
                    />
                  </div>
                )}

                <div className="row g-4">
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">
                      <i className="bi bi-person me-2"></i>
                      Student Information
                    </h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td className="text-muted" style={{ width: '40%' }}>Name</td>
                          <td className="fw-semibold">{viewCertificate.studentName}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Enrollment ID</td>
                          <td><code>{viewCertificate.enrollmentId}</code></td>
                        </tr>
                        <tr>
                          <td className="text-muted">Class</td>
                          <td>{viewCertificate.class}-{viewCertificate.section}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Roll Number</td>
                          <td>{viewCertificate.rollNumber || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="text-primary mb-3">
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Certificate Information
                    </h6>
                    <table className="table table-sm">
                      <tbody>
                        <tr>
                          <td className="text-muted" style={{ width: '40%' }}>Template</td>
                          <td className="fw-semibold">{viewCertificate.templateName}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Generated At</td>
                          <td>{formatDate(viewCertificate.generatedAt)}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">School</td>
                          <td>{getSchoolName()}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {Object.keys(viewCertificate.customFields).length > 0 && (
                  <div className="mt-4">
                    <h6 className="text-primary mb-3">
                      <i className="bi bi-card-list me-2"></i>
                      Additional Details
                    </h6>
                    <div className="row g-2">
                      {Object.entries(viewCertificate.customFields).map(([key, value]) => (
                        <div key={key} className="col-md-6">
                          <div className="bg-light rounded p-2">
                            <small className="text-muted d-block">{getCustomFieldLabel(key)}</small>
                            <span className="fw-semibold">{value || 'N/A'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setViewCertificate(null)}>
                  Close
                </button>
                {viewCertificate.templateUrl && (
                  <a
                    href={viewCertificate.templateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <i className="bi bi-download me-2"></i>
                    View Template
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateHistory;
